import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import {
  loadAnalytics,
  readConsent,
  sendPageView,
  writeConsent,
  type ConsentDecision,
} from '@/lib/analytics';

const PRIVACY_POLICY_URL = 'https://taurusystems.com/privacy/';

const CHOICE_BUTTON_CLASS =
  'flex-1 rounded-md bg-tres-action px-4 py-2 text-sm font-medium text-white hover:opacity-90';

/** Asks for the visitor's analytics decision on an instance that configures a
 *  GA4 measurement id, then runs GA4 only if they accepted. With no id it
 *  renders nothing and loads nothing. */
export function CookieConsent({ measurementId }: { measurementId: string | null }) {
  const [decision, setDecision] = useState<ConsentDecision | null>(() =>
    measurementId ? readConsent() : null,
  );
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (measurementId && decision === 'granted') loadAnalytics(measurementId);
  }, [measurementId, decision]);

  useEffect(() => {
    if (measurementId && decision === 'granted') sendPageView(pathname);
  }, [measurementId, decision, pathname, search]);

  if (!measurementId || decision !== null) return null;

  const choose = (next: ConsentDecision) => {
    writeConsent(next);
    setDecision(next);
  };

  return (
    <section
      aria-label="Cookie consent"
      className="fixed inset-x-4 bottom-4 z-50 rounded-md border border-border bg-card p-4 text-sm text-tres-text-primary shadow-lg sm:right-auto sm:max-w-md"
    >
      <h2 className="font-semibold">Cookies on TReS Preview</h2>
      <p className="mt-1 text-tres-text-secondary">
        This site uses necessary storage to work. With your permission, it also uses Google
        Analytics cookies to see how visitors use it. We do not use advertising cookies.
      </p>
      <a
        href={PRIVACY_POLICY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 inline-block text-tres-action hover:underline"
      >
        Privacy Policy
      </a>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => choose('granted')} className={CHOICE_BUTTON_CLASS}>
          Accept analytics
        </button>
        <button type="button" onClick={() => choose('denied')} className={CHOICE_BUTTON_CLASS}>
          Reject
        </button>
      </div>
    </section>
  );
}
